import { useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useQuery, useMutation } from '@zenbusiness/application-commons-graphql';
import Caption from '@zenbusiness/common-components/Caption';
import SharedAsset from '@zenbusiness/common-components/SharedAsset';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import format from 'date-fns/format';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';

import Button from '@mui/material/Button';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import Breadcrumbs from '#client/components/Breadcrumbs';
import PrivacyMap from '#client/components/PrivacyMap';
import { buildCartUrl } from '#client/utils/urls';

import ContactCards from './ContactCards';
import DnsCards from './DnsCards';
import NameserverCards from './NameserverCards';

import { DOMAIN_VIEW_READ, CONNECT_DOMAIN_TO_DUDA, REMOVE_DOMAIN } from './constants';
import {
  StyledButton, StyledButtonClose, StyledButtonWide, StyledCard, StyledCardActionsQuickSelectAction,
  StyledCardQuickSelectContent, StyledDialog, StyledDialogButtonGroup,
  StyledDialogButtons, StyledDialogHeader, StyledDialogTitle, StyledGridDomainHead,
  StyledGridQuickSelectTitle, StyledSectionRoot, StyledSharedAssetHintsIcon, StyledPageTitle
} from './styles';

const shouldRedirect = (loading, connectToWebsiteLoading, connectToWebsiteData, domain) => {
  if (loading || connectToWebsiteLoading || connectToWebsiteData) { return false; }
  if (domain.transferred || domain.deleted) { return true; }
  return !domain.isExternal && !domain.verified;
};

const Domain = () => {
  const theme = useTheme();
  const mediaQuery = useMediaQuery(theme.breakpoints.up('sm'));
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid } = useParams();
  const [dialogOpen, updateOpenDialog] = useState(false);
  const { loading: readLoading, data } = useQuery(DOMAIN_VIEW_READ, {
    variables: {
      domainUuid,
      businessEntityUuid
    }
  });

  const [connectToWebsite, {
    loading: connectToWebsiteUpdateLoading,
    data: connectToWebsiteUpdateData
  }] = useMutation(CONNECT_DOMAIN_TO_DUDA);

  const [removeDomain] = useMutation(REMOVE_DOMAIN);

  const loading = readLoading || connectToWebsiteUpdateLoading;

  const domain = loading ? {} : (
    connectToWebsiteUpdateData && connectToWebsiteUpdateData.connectToWebsite
  ) || data.domain;

  const privacyUrl = buildCartUrl('domain_name_privacy', {
    businessEntityUuid,
    domainName: domain?.domainName
  });
  const isExternal = loading ? null : domain?.isExternal === true;
  const isInternal = loading ? null : domain?.isExternal === false;

  const websites = loading ? {} : data.websites.websites;
  const website = websites[0] || null;
  const hasPublished = website?.publishStatus === 'PUBLISHED';

  const websiteConnectionStatus = connectToWebsiteUpdateData
    ? (connectToWebsiteUpdateData?.connectToWebsite?.websiteConnectionStatus
      || connectToWebsiteUpdateData?.connectToDudaWebsite?.websiteConnectionStatus)
    : domain?.websiteConnectionStatus;

  const unconnected = websiteConnectionStatus === null;
  const disconnected = websiteConnectionStatus === 'disconnected';
  const connected = websiteConnectionStatus === 'connected';
  const connecting = websiteConnectionStatus === 'ready_for_verification';
  const connectToWebsiteIconUrl = connecting
    ? '/icon/product/domain-name-reg-256.svg'
    : '/icon/product/static-website-256.svg';
  const ownerContact = domain?.domainContacts?.find(({ contactType: type }) => (type === 'owner'));

  const handleDomainWebsiteConnection = () => {
    connectToWebsite({
      variables: {
        input: {
          websiteUuid: website.websiteUuid,
          domainUuid
        }
      }
    });
  };

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const trackRemoveDomain = useLinkPressedCallback({
    elementName: 'remove_domain',
    linkDestination: 'none',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  const handleRemoveDomain = async () => {
    await removeDomain({
      variables: {
        input: {
          domainUuid
        }
      }
    });
    trackRemoveDomain();
  };

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const trackClickOpen = useLinkPressedCallback({
    elementName: 'remove_button',
    linkDestination: 'none',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  const handleRemoveButton = () => {
    updateOpenDialog(true);
    trackClickOpen();
  };

  const handleClose = () => {
    updateOpenDialog(false);
  };

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const handleOnClick = useLinkPressedCallback({
    elementName: 'get_domain_privacy',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const handleManageClick = useLinkPressedCallback({
    elementName: 'manage_website',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const handleHelpClick = useLinkPressedCallback({
    elementName: 'view_help_center',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  // TODO: Change useLinkPressedCallback by a button_pressed - Clicks
  const handleLearnMoreClick = useLinkPressedCallback({
    elementName: 'learn_more_button',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  const handleDomainBreadcrumbClick = useLinkPressedCallback({
    elementName: 'domains_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  const handleOverviewBreadcrumbClick = useLinkPressedCallback({
    elementName: 'overview_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'domain_settings_page'
  });

  if (shouldRedirect(loading, connectToWebsiteUpdateLoading, connectToWebsiteUpdateData, domain)) {
    return (<Navigate to={`/d/company/${businessEntityUuid}/marketing-tools/domains`} />);
  }

  if (!loading && connectToWebsiteUpdateData) {
    if (isExternal) {
      return (<Navigate to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/external-records`} />);
    }
  }

  return (
    <StyledSectionRoot>
      <Breadcrumbs gutterBottom>
        <Button variant="text" color="primary" component={RouterLink} onClick={handleOverviewBreadcrumbClick} to={`/d/company/${businessEntityUuid}/marketing-tools`}>Overview</Button>
        <Button variant="text" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains`}>Domains</Button>
        <Button variant="text" disabled color="primary" onClick={handleDomainBreadcrumbClick} component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>Domain</Button>
      </Breadcrumbs>
      <StyledGridDomainHead
        container
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Grid item>
          <StyledPageTitle level={1} variant="lg-alt">
            Domain Settings
          </StyledPageTitle>
        </Grid>
        {domain?.srsExpireDatetime && (
        <Grid item>
          <Caption size="lg" color="focus50" align="right">
            Renewal Date
          </Caption>
          <Text size="sm" align="right">
            {format(new Date(domain.srsExpireDatetime), 'MM/dd/yyyy')}
          </Text>
        </Grid>
        )}
      </StyledGridDomainHead>
      <Grid
        container
        item
        xs
      >
        <Grid
          container
          item
          direction="row"
          justifyContent="flex-start"
          alignItems="stretch"
          spacing={2}
        >
          {/* Quick setup */}
          {(domain && domain.active && website && hasPublished) && (
          <Grid container item xs={12} md={6}>
            <StyledCard>
              <StyledGridQuickSelectTitle>
                <Title level={3} variant="sm">
                  {connected && 'Website is Connected'}
                  {connecting && 'Verification'}
                  {(unconnected || disconnected) && 'Connect to Website'}
                </Title>
                <Divider />
              </StyledGridQuickSelectTitle>
              <StyledCardQuickSelectContent sx={{
                ...(connecting && {
                  '& img': {
                    maxHeight: '100px',
                    paddingBottom: '10px'
                  },
                  '& p': {
                    marginBottom: '0px'
                  }
                })
              }}
              >
                <StyledSharedAssetHintsIcon src={connectToWebsiteIconUrl} width={115} alt="website" />
                <Caption
                  size="lg"
                  color="focus50"
                  align="center"
                  sx={{
                    ...(disconnected && {
                      color: '#EC7C64',
                      fontWeight: 800
                    })
                  }}
                >
                  {connected && (
                    'This domain is connected to your ZenBusiness website'
                  )}
                  {unconnected && (
                    'Connect this domain to my ZenBusiness website'
                  )}
                  {disconnected && (
                    'This domain is no longer connected to your ZenBusiness website'
                  )}
                  {connecting && (
                    'Verifying the connection between your external domain and ZenBusiness website can take between 24-48 hours.'
                  )}
                </Caption>
                {connecting && (
                  <Text
                    size="sm"
                    color="focus50"
                    align="center"
                  >
                    Our system will continue testing for a successful connection,
                    and once a successful connection has been made,
                    you will be able to visit your domain and see your website.
                  </Text>
                )}
              </StyledCardQuickSelectContent>
              {!connecting && (
                <StyledCardActionsQuickSelectAction>
                  {connected && (
                  <StyledButton
                    size="medium"
                    variant="outlined"
                    color="primary"
                    component={RouterLink}
                    to={`/d/company/${businessEntityUuid}/marketing-tools/websites`}
                    onClick={handleManageClick}
                  >
                    Manage Website
                  </StyledButton>
                  )}
                  {(unconnected || disconnected) && (
                    <StyledButton
                      size="medium"
                      variant="outlined"
                      color={unconnected ? 'primary' : 'secondary'}
                      disabled={loading}
                      onClick={handleDomainWebsiteConnection}
                    >
                      {disconnected && 'Re-connect'}
                      {unconnected && 'Connect'}
                    </StyledButton>
                  )}
                </StyledCardActionsQuickSelectAction>
              )}
            </StyledCard>
          </Grid>
          )}

          {/* Domain Privacy */}
          {isInternal
            && (
            <Grid container item xs={12} md={6}>
              <StyledCard variant="outlined">
                <StyledGridQuickSelectTitle>
                  <Grid
                    container
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Grid item>
                      <Text size="md">
                        Domain Privacy
                      </Text>
                    </Grid>
                    <Grid item>
                      {domain?.domainPrivacy ? (
                        <>
                          <Text size="md" component="span">
                            On
                          </Text>
                        </>
                      ) : (
                        <>
                          <Text size="md" component="span">
                            Off
                          </Text>

                        </>
                      )}
                    </Grid>
                  </Grid>
                </StyledGridQuickSelectTitle>
                <StyledCardQuickSelectContent>
                  <Grid container direction="column">
                    <Grid item>
                      {domain && !loading && (
                      <PrivacyMap domainPrivacy={domain.domainPrivacy} contact={ownerContact} />
                      )}
                    </Grid>
                    <Grid item align="center">
                      <Caption size="lg">
                        {domain?.domainPrivacy ? (
                          'Domain privacy keeps your contact information from being publicly listed.'
                        ) : (
                          'Your Domain contact info is publicly listed. Get Domain Privacy to hide it.'
                        )}
                      </Caption>
                    </Grid>
                  </Grid>
                </StyledCardQuickSelectContent>
                <StyledCardActionsQuickSelectAction>
                  {domain && domain.domainPrivacy ? (
                    <StyledButton size="medium" variant="text" onClick={handleLearnMoreClick} component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/privacy`}>
                      Learn More
                    </StyledButton>
                  ) : (
                    <StyledButton
                      size="medium"
                      color="secondary"
                      href={privacyUrl}
                      onClick={handleOnClick}
                    >
                      Get Domain Privacy
                    </StyledButton>
                  )}
                </StyledCardActionsQuickSelectAction>
              </StyledCard>
            </Grid>
            )}

          {isInternal
          && (
            <Grid container item xs={12} md={6}>
              <StyledCard variant="outlined">
                <StyledCardQuickSelectContent>
                  <StyledSharedAssetHintsIcon src="/icon/misc/faq-3.svg" width={300} alt="help" />
                  <Text size="md">
                    Helpful Hints
                  </Text>
                  <Caption size="lg" align="center">
                    Looking for domain help? Take a look in our help center.
                  </Caption>
                </StyledCardQuickSelectContent>
                <StyledCardActionsQuickSelectAction>
                  <StyledButtonWide size="medium" color="primary" variant="contained" onClick={handleHelpClick} target="_blank" rel="noopener" href="https://intercom.help/zenbusiness/en/collections/2306593-zenbusiness-domains-websites-and-email">
                    View Help Center
                  </StyledButtonWide>
                </StyledCardActionsQuickSelectAction>
              </StyledCard>
            </Grid>
          )}

          {true
          && (
            <>
              <StyledDialog
                open={dialogOpen}
                onClose={handleClose}
              >
                <StyledDialogHeader>
                  <StyledButtonClose
                    aria-label="close"
                    onClick={handleClose}
                    size="large"
                  >
                    <SharedAsset src="/icon/micro/close-x.svg" width={16} alt="help" />
                  </StyledButtonClose>
                </StyledDialogHeader>
                <DialogTitle>
                  <StyledDialogTitle component="p" variant="lg" level={3}>
                    Remove Domain
                  </StyledDialogTitle>
                </DialogTitle>
                <DialogContent>
                  <Caption id="alert-dialog-description" size="lg" color="focus50" align="center">
                    Do you want to remove this domain from your ZenBusiness account?
                  </Caption>
                </DialogContent>
                <StyledDialogButtonGroup>
                  <StyledDialogButtons onClick={handleClose} color="secondary" variant={mediaQuery ? 'outlined' : 'text'}>
                    Cancel
                  </StyledDialogButtons>
                  <StyledDialogButtons onClick={handleRemoveDomain} color="secondary" variant="contained">
                    Remove
                  </StyledDialogButtons>
                </StyledDialogButtonGroup>
              </StyledDialog>
              <Grid item xs={12} md={12} lg={6}>
                <StyledCard variant="outlined">
                  <StyledGridQuickSelectTitle>
                    <Title level={3} variant="sm">
                      Remove this Domain
                    </Title>
                    <Divider />
                  </StyledGridQuickSelectTitle>
                  <StyledCardQuickSelectContent>
                    <StyledSharedAssetHintsIcon src="/icon/misc/no-256.svg" width={115} alt="No" />
                    <Caption size="lg" color="focus50" align="center">
                      Do you want to remove this domain from your ZenBusiness account?
                    </Caption>
                  </StyledCardQuickSelectContent>
                  <StyledCardActionsQuickSelectAction>
                    <StyledButton
                      size="medium"
                      variant="outlined"
                      color="secondary"
                      onClick={handleRemoveButton}
                    >
                      <Text color="energy30">
                        Remove
                      </Text>
                    </StyledButton>
                  </StyledCardActionsQuickSelectAction>
                </StyledCard>
              </Grid>
            </>
          )}
        </Grid>

        {isInternal && (
        <>
          <Grid container direction="row">
            <Grid item xs={12}>
              <NameserverCards item domain={domain} />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={12}>
              <DnsCards domain={domain} />
            </Grid>
          </Grid>
          <Grid container direction="row">
            <Grid item xs={12}>
              <ContactCards item domain={domain} />
            </Grid>
          </Grid>
        </>
        )}
      </Grid>
    </StyledSectionRoot>
  );
};

export default Domain;
